import { useCallback } from 'react';
import { TextInput, useMantineTheme } from '@mantine/core';
import type { ZodTypeAny } from 'zod';
import { z } from 'zod';

import type { BaseInterativeField, FieldDescriptor, StaticFieldProps } from '../../types/common';
import bannerWordsRefine from '../utils/bannedWordsRefine';
import { FieldMeta } from '../common/FieldMeta';

export type TextFieldType = BaseInterativeField & {
  type: 'TextField';
  validation?: {
    bannedWords?: string[];
    bannedWordsHidden?: string[];
    softWords?: string[];
  };
};

function TextFieldElement({
  field,
  value,
  onValueChange,
  errors,
  isTouched,
}: StaticFieldProps<TextFieldType>) {
  const theme = useMantineTheme();

  const handleOnChange = useCallback(
    (e) => {
      const { target } = e;

      onValueChange(target.value);
    },
    [onValueChange]
  );

  const isError = errors && isTouched;

  return (
    <div>
      <FieldMeta field={field} errors={errors} isTouched={isTouched} indicator={field.indicator} />
      <TextInput
        value={typeof value === 'string' ? value : ''}
        onChange={handleOnChange}
        styles={{
          input: {
            minHeight: 48,
            borderColor: isError ? theme.colors.orange[3] : theme.colors.gray2[2],
            borderRadius: theme.radius.md,
            '&::placeholder': {
              color: isError ? theme.colors.orange[3] : 'inherit',
            },
          },
        }}
      />
    </div>
  );
}

const createValidationSchema = (field: TextFieldType) => {
  let fieldSchema: ZodTypeAny = z
    .string({
      required_error: `${field.label.charAt(0).toUpperCase() + field.label.slice(1).toLowerCase()} field is required`,
    })
    .trim();

  if (field.validation?.isOptional) {
    fieldSchema = fieldSchema.optional();
  } else {
    // @ts-expect-error Not sure how to type this
    fieldSchema = fieldSchema.min(1, {
      message: `${field.label.charAt(0).toUpperCase() + field.label.slice(1).toLowerCase()} field is required`,
    });
  }

  if (field.validation?.bannedWords) {
    fieldSchema = fieldSchema.refine(bannerWordsRefine(field.validation.bannedWords), (value) => ({
      message: `Your ${field.label.toLowerCase()} cannot contain the following phrase: ${value}`,
    }));
  }

  if (field.validation?.bannedWordsHidden) {
    fieldSchema = fieldSchema.refine(
      bannerWordsRefine(field.validation.bannedWordsHidden, field.validation.softWords, {
        cleanValue: false,
      }),
      {
        message: `Your ${field.label.toLowerCase()} has a word or phrase that you cannot use.`,
      }
    );
  }

  return fieldSchema;
};

const Descriptor: FieldDescriptor<TextFieldType> = {
  FieldComponent: TextFieldElement,
  name: 'TextField',
  createValidationSchema,
};

export default Descriptor;
